import { useCallback, useState } from "react";
import { Upload, FileText, AlertTriangle, CheckCircle2, X, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

// Static label table — keeps the warning roll-up readable in the UI without
// shipping the i18n catalogue overhead for an admin-only surface.
const WARNING_LABELS = {
  missing_legal_name: "Missing legal name",
  no_entity_identifier_supplied: "No entity identifier",
  country_inferred_from_freetext: "Country inferred from free-text",
  country_unrecognised: "Country unrecognised",
};

export function CustomerMasterUpload() {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [lastFile, setLastFile] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);

  const downloadTemplate = useCallback(async () => {
    try {
      const { data } = await api.get(
        "/canonical/supplier/customer-master/template",
      );
      const blob = new Blob([data.csv_template], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "erio-customer-master-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Failed to fetch template",
      );
    }
  }, []);

  const upload = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setCommitted(null);
    setLastFile(file);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        "/canonical/supplier/customer-master/preview",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      const { rows_clean, rows_mapped } = data.summary || {};
      toast.success(
        `Mapped ${rows_mapped} rows (${rows_clean} clean, ${rows_mapped - rows_clean} with warnings).`,
      );
    } catch (e) {
      const detail = formatApiErrorDetail(e?.response?.data?.detail);
      setError(detail || "Upload failed");
      toast.error(detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  }, []);

  const commit = useCallback(async () => {
    if (!lastFile || !result) return;
    const rowsMapped = result?.summary?.rows_mapped ?? 0;
    if (
      !window.confirm(
        `Persist ${rowsMapped} mapped suppliers into your tenant's supplier list?\n\n` +
          `Re-uploading the same CSV later updates existing rows (matched on external_id) ` +
          `rather than creating duplicates.`,
      )
    )
      return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", lastFile);
      const { data } = await api.post(
        "/canonical/supplier/customer-master/commit",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setCommitted(data.summary);
      toast.success(
        `${data.summary.upserted} suppliers upserted into your tenant.`,
      );
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Commit failed",
      );
    } finally {
      setCommitting(false);
    }
  }, [lastFile, result]);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) upload(file);
    },
    [upload],
  );

  return (
    <section
      className="mt-8 rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="customer-master-upload"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-ek-purple" />
            <h2 className="font-display text-base font-semibold text-ek-ink">
              BYO supplier list (CSV)
            </h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
              customer_master:v1
            </span>
          </div>
          <p className="mt-1 max-w-2xl text-xs text-ek-ink/70">
            Upload your own supplier master file (CSV) for prospects whose
            entities aren't in public registries. Headers are matched
            case-insensitively against a flexible alias table — see the
            template. Nothing is persisted; this is a preview/mapping check.
          </p>
        </div>
        <button
          type="button"
          onClick={downloadTemplate}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-ek-ink hover:bg-slate-50"
          data-testid="customer-master-download-template"
        >
          <Download size={13} />
          Template CSV
        </button>
      </div>

      <label
        htmlFor="cm-upload-input"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`mt-4 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          dragOver
            ? "border-ek-purple bg-ek-purple/5"
            : "border-slate-200 bg-slate-50 hover:border-ek-purple/40"
        }`}
        data-testid="customer-master-dropzone"
      >
        <Upload size={22} className="text-ek-purple" />
        <p className="text-sm font-medium text-ek-ink">
          Drop a CSV here or click to choose
        </p>
        <p className="text-[11px] text-ek-ink/60">
          UTF-8 · up to 2 MB · up to 5,000 rows per preview
        </p>
        <input
          id="cm-upload-input"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          disabled={busy}
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="customer-master-file-input"
        />
      </label>

      {busy && (
        <p
          className="mt-3 text-xs text-ek-ink/70"
          data-testid="customer-master-busy"
        >
          Mapping rows into canonical shape…
        </p>
      )}

      {error && (
        <div
          className="mt-3 flex items-center gap-2 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800"
          data-testid="customer-master-error"
        >
          <AlertTriangle size={14} />
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-rose-600 hover:text-rose-900"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-3" data-testid="customer-master-result">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Tile
              label="Rows mapped"
              value={result.summary.rows_mapped}
              testid="cm-rows-mapped"
            />
            <Tile
              label="Clean"
              value={result.summary.rows_clean}
              good
              testid="cm-rows-clean"
            />
            <Tile
              label="With warnings"
              value={
                result.summary.rows_mapped - result.summary.rows_clean
              }
              warn={
                result.summary.rows_mapped - result.summary.rows_clean > 0
              }
              testid="cm-rows-warned"
            />
            <Tile
              label="Warnings (total)"
              value={result.summary.warnings_count}
              warn={result.summary.warnings_count > 0}
              testid="cm-warnings-total"
            />
          </div>

          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-ek-purple/20 bg-ek-purple/5 p-3"
            data-testid="customer-master-commit-bar"
          >
            <div className="text-[11.5px] text-ek-ink/75">
              {committed ? (
                <span>
                  <CheckCircle2
                    size={13}
                    className="-mt-0.5 mr-1 inline text-emerald-600"
                  />
                  {committed.upserted} suppliers upserted into your tenant.
                  Re-upload to update them.
                </span>
              ) : (
                <span>
                  Happy with the mapping? Persist these {result.summary.rows_mapped} rows
                  into your tenant's supplier list. Idempotent on{" "}
                  <code className="font-mono">external_id</code>.
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={commit}
              disabled={committing || !!committed}
              className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-1.5 text-xs font-medium text-white hover:bg-ek-purple/90 disabled:opacity-50"
              data-testid="customer-master-commit"
            >
              {committing ? "Persisting…" : committed ? "Persisted" : "Persist to suppliers"}
            </button>
          </div>

          {Object.keys(result.summary.warnings_by_type || {}).length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-center gap-2 text-xs font-medium text-amber-800">
                <AlertTriangle size={13} /> Warnings by type
              </div>
              <ul
                className="mt-1.5 space-y-0.5 text-[11.5px] text-amber-900"
                data-testid="cm-warnings-by-type"
              >
                {Object.entries(result.summary.warnings_by_type).map(
                  ([tag, count]) => (
                    <li key={tag} className="flex justify-between gap-3">
                      <span>{WARNING_LABELS[tag] || tag}</span>
                      <span className="font-medium">{count}</span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          )}

          <details className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
            <summary className="cursor-pointer font-medium text-ek-ink">
              First 10 mapped records (engine view)
            </summary>
            <div className="mt-2 overflow-x-auto">
              <table className="min-w-full text-[11px]" data-testid="cm-records-table">
                <thead className="text-ek-ink/65">
                  <tr>
                    <th className="px-2 py-1 text-left">Legal name</th>
                    <th className="px-2 py-1 text-left">Country</th>
                    <th className="px-2 py-1 text-left">IDs</th>
                    <th className="px-2 py-1 text-left">Active</th>
                    <th className="px-2 py-1 text-left">Distress</th>
                    <th className="px-2 py-1 text-left">Warnings</th>
                  </tr>
                </thead>
                <tbody>
                  {result.records.slice(0, 10).map((r) => (
                    <tr key={r.external_id} className="border-t border-slate-200">
                      <td className="px-2 py-1 font-medium text-ek-ink">
                        {r.legal_name}
                      </td>
                      <td className="px-2 py-1">{r.country || "—"}</td>
                      <td className="px-2 py-1 text-ek-ink/70">
                        {[
                          r.ids?.duns && `DUNS:${r.ids.duns}`,
                          r.ids?.lei && `LEI:${r.ids.lei.slice(0, 8)}…`,
                          r.ids?.companies_house_id &&
                            `CRN:${r.ids.companies_house_id}`,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </td>
                      <td className="px-2 py-1">
                        {r.active === true ? (
                          <CheckCircle2 size={12} className="text-emerald-600" />
                        ) : r.active === false ? (
                          <X size={12} className="text-rose-600" />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-2 py-1">
                        {r.financial_distress?.band || "unknown"}
                      </td>
                      <td className="px-2 py-1 text-ek-ink/70">
                        {r.mapping_warnings?.length || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
    </section>
  );
}

const Tile = ({ label, value, good, warn, testid }) => (
  <div
    className={`rounded-md border p-3 ${
      warn
        ? "border-amber-200 bg-amber-50"
        : good
          ? "border-emerald-200 bg-emerald-50"
          : "border-slate-200 bg-white"
    }`}
    data-testid={testid}
  >
    <p className="font-display text-lg font-semibold text-ek-ink">{value}</p>
    <p className="text-[10px] uppercase tracking-wide text-ek-ink/60">
      {label}
    </p>
  </div>
);
